// 보상 카드 뽑기 화면: 뒷면 카드 3장이 회전하며 날아와 깔린다 → 10초 카운트다운(원형 게이지) → 눌러 고르면 카드가 들려 올라가 3D 뒤집힘 →
// 등급 빛(일반 흰·희귀 파랑·영웅 보라·전설 금 + 전설은 한 번 흔들림·광선) → 나머지 카드가 차례로 뒤집혀 「놓친 보상」 공개.
// 시간이 다 되면 자동 선택. 한 번 고르면 더 받지 않는다(중복·연타 방지). 고르는 명령은 호출자(Screens → GameCommand pickCard)가 보낸다.
// 애니메이션은 CSS 전환·WAAPI뿐이고 프레임마다 도는 JS는 카운트다운 숫자 갱신(update)뿐이다(객체를 만들지 않는다).
import { assetUrl } from '../pwa/assetMap';
import { el } from './dom';
import { installClearStyle } from './clearStyle';
import { SparkField } from './sparkField';
import { uiSfx } from './clearFx';
import {
  Countdown, PICK_SECONDS, RARITY_FX, TIMELINE, allRevealedMs, autoPickIndex, cardFaceOf, chosenRevealedMs, flyStartMs, landedMs, otherFlipMs, particleBudget, type CardFace,
} from './rewardCardsModel';

/** 프레임마다 문자열을 만들지 않도록 게이지 값(0.5% 단위)과 초 숫자를 미리 만들어 둔다 */
const RING_OFFSET: readonly string[] = Array.from({ length: 201 }, (_, i) => String(i / 2));
const SECOND_TEXT: readonly string[] = Array.from({ length: 100 }, (_, i) => String(i));

export type PickPhase = 'idle' | 'fly' | 'pick' | 'reveal' | 'done';

export interface OpenOptions {
  resultId: string;
  gold: readonly number[];
  loot: readonly string[];
  gearName: (id: string) => string;
  reduceFlash: boolean;
  reducedMotion: boolean;
  /** 날아오기 없이 바로 깔기(QA·이전 스크립트용 ?fx=off) */
  instant: boolean;
  /** 제한 시간(초). 기본 PICK_SECONDS */
  seconds?: number;
}

interface CardEl { root: HTMLDivElement; inner: HTMLDivElement; front: HTMLDivElement; tag: HTMLDivElement; rays: HTMLImageElement; face: CardFace | null }

/**
 * 결과 카드 그림(2026-11-03 vfx-look-art, ChatGPT 웹 GUI 스타일 B 키트, public/assets/ui/ui-art-manifest.json id result-*).
 * 앞면 틀은 아래쪽에 이름판이 있어 이름을 그 판 위에 올린다(clearStyle .dh-pk-front.art). 예전 ui/cards/frame-* 는 남겨 둔다.
 */
export const CARD_BACK = 'ui/result/card-back.webp';
export const CARD_HIGHLIGHT = 'ui/result/card-highlight.webp';
export const CARD_FRAMES: readonly string[] = ['ui/result/card-front.webp', 'ui/result/card-rare.webp', 'ui/result/card-epic.webp', 'ui/result/card-legendary.webp'];

export class RewardCards {
  readonly root: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly timer: HTMLDivElement;
  private readonly ring: SVGCircleElement;
  private readonly secText: HTMLElement;
  private readonly row: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly flash: HTMLDivElement;
  private readonly skip: HTMLDivElement;
  private readonly cards: CardEl[] = [];
  private readonly sparks: SparkField;
  private readonly cd = new Countdown();
  private anims: Animation[] = [];
  private timers: number[] = [];
  private onPick: (index: number, auto: boolean) => void = () => {};
  private onClosed: () => void = () => {};
  private opts: OpenOptions | null = null;
  private lastSec = -1;
  private lastOff = -1;
  private lastTs = 0;
  private revealedAt = 0;
  private chosen = -1;
  /** 다른 탭의 선택으로 공개를 바로잡았다 */
  private corrected = false;
  phase: PickPhase = 'idle';
  /** QA: 자동 선택 횟수·열린 횟수 */
  autoPicks = 0;
  opened = 0;

  constructor(private readonly parent: HTMLElement, reduced = false) {
    installClearStyle();
    const root = el('div', '', parent);
    root.className = 'dh-pk'; root.dataset.testid = 'pick-stage'; root.setAttribute('role', 'dialog'); root.setAttribute('aria-label', '보상 카드 뽑기');
    this.root = root;
    const head = el('div', '', root); head.className = 'dh-pk-head';
    this.title = el('div', '', head, '보상 카드를 고르세요'); this.title.className = 'dh-pk-title';
    this.timer = el('div', '', head); this.timer.className = 'dh-pk-timer'; this.timer.dataset.testid = 'pick-timer';
    this.timer.innerHTML = '<svg viewBox="0 0 44 44"><circle class="bg" cx="22" cy="22" r="19"/><circle class="fg" cx="22" cy="22" r="19" pathLength="100"/></svg><b>10</b>';
    this.ring = this.timer.querySelector('.fg') as SVGCircleElement;
    this.secText = this.timer.querySelector('b') as HTMLElement;
    this.row = el('div', '', root); this.row.className = 'dh-pk-row';
    for (let i = 0; i < 3; i++) this.cards.push(this.buildCard(i));
    this.hint = el('div', '', root); this.hint.className = 'dh-pk-hint'; this.hint.dataset.testid = 'pick-hint';
    this.sparks = new SparkField(root, particleBudget(parent.clientWidth || 1280, parent.clientHeight || 720, reduced));
    this.flash = el('div', '', root); this.flash.className = 'dh-pk-flash';
    this.skip = el('div', '', root, '화면을 누르면 계속'); this.skip.className = 'dh-pk-skip';
    // 모두 공개된 뒤 아무 곳이나 누르면 바로 닫는다(카드 위 클릭은 버블링으로 여기까지 오지만 phase가 'done'일 때만 닫는다)
    root.addEventListener('click', () => { if (this.phase === 'reveal' && this.revealedAt > 0 && performance.now() >= this.revealedAt) this.close(); });
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('resize', this.layout);
  }

  /** 새 결과 카드를 열고 날아오게 한다. pick(index, auto)로 고른 칸을 알린다 */
  open(o: OpenOptions, onPick: (index: number, auto: boolean) => void, onClosed: () => void): void {
    this.cancel();
    this.opts = o; this.onPick = onPick; this.onClosed = onClosed; this.chosen = -1; this.revealedAt = 0; this.lastSec = -1; this.lastOff = -1; this.lastTs = 0;
    this.opened++;
    this.cd.stop();
    const secs = o.seconds ?? PICK_SECONDS;
    (this.cd as { totalMs: number }).totalMs = secs * 1000;
    this.cd.left = secs * 1000;
    const root = this.root;
    root.style.display = 'flex'; root.style.opacity = '1';
    this.layout();
    this.title.textContent = '보상 카드를 고르세요';
    this.setHint('카드 한 장을 고르세요', '');
    this.timer.style.visibility = 'visible';
    this.paintTimer();
    this.skip.classList.remove('on');
    for (let i = 0; i < 3; i++) {
      const c = this.cards[i];
      const face = cardFaceOf(o.loot[i] ?? '', o.gold[i] ?? 0, o.gearName);
      c.face = face;
      this.fillFront(c, face);
      c.root.className = 'dh-pk-card lock'; c.root.style.cssText = '';
      c.root.style.setProperty('--rc', face.color);
      c.root.style.setProperty('--glow', RARITY_FX[face.rarity].glowPx + 'px');
      c.root.setAttribute('aria-disabled', 'true'); c.root.tabIndex = -1;
      c.tag.textContent = '';
      c.front.setAttribute('aria-hidden', 'true'); // 고르기 전에는 앞면 내용을 읽지 않는다(스크린 리더·DOM 모두)
    }
    this.corrected = false;
    const total = landedMs(3);
    if (o.instant || o.reducedMotion) {
      this.phase = 'pick';
      this.unlock();
    } else {
      this.phase = 'fly';
      this.flyIn();
      this.later(total, () => { if (this.phase === 'fly') { this.phase = 'pick'; this.unlock(); } });
    }
  }

  /** 프레임마다 호출(dt 계산 포함): 카운트다운을 흘리고 숫자·게이지를 갱신. 눌러서 고르면 pickLocal이 먼저 반응한다 */
  update(frozen = false): void {
    if (this.phase !== 'pick') return;
    // 일시정지·설정 창이 떠 있는 동안은 시간이 흐르지 않는다(풀리면 그 순간부터 다시 센다)
    if (frozen) { this.lastTs = 0; return; }
    const now = performance.now();
    const dt = this.lastTs === 0 ? 0 : now - this.lastTs;
    this.lastTs = now;
    const fired = this.cd.step(dt);
    this.paintTimer();
    if (fired) {
      // 시간 초과: 영수증 id로 정한 칸을 자동 선택
      this.autoPicks++;
      this.choose(autoPickIndex(this.opts?.resultId ?? ''), true);
    }
  }

  /** 카드를 눌렀다(마우스·터치·키). 이미 골랐거나 아직 날아오는 중이면 아무 일도 없다 */
  tryPick(i: number): boolean {
    if (this.phase !== 'pick' || i < 0 || i >= 3) return false;
    this.choose(i, false);
    return true;
  }

  get chosenIndex(): number { return this.chosen; }
  /** QA: 남은 초 */
  get secondsLeft(): number { return this.cd.seconds; }

  /** 장면을 벗어났다: 모든 연출을 끊고 숨긴다(onClosed 없음) */
  cancel(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    for (const a of this.anims) { try { a.cancel(); } catch { /* 이미 끝남 */ } }
    this.anims.length = 0;
    this.cd.stop();
    this.phase = 'idle'; this.chosen = -1;
    this.root.style.display = 'none';
    this.sparks.clear();
  }

  /** 모두 공개됐거나 건너뛰었다: 서서히 닫고 결과 표로 돌아간다 */
  close(): void {
    if (this.phase === 'done' || this.phase === 'idle') return;
    this.phase = 'done';
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    // 공개가 덜 끝났어도 모든 카드를 앞면으로
    for (const c of this.cards) { c.root.classList.add('flipped'); c.front.removeAttribute('aria-hidden'); }
    const a = this.root.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 360, easing: 'ease-in', fill: 'forwards' });
    this.anims.push(a);
    a.onfinish = () => { if (this.phase === 'done') { this.root.style.display = 'none'; this.sparks.clear(); } };
    this.onClosed();
  }

  /**
   * 다른 탭(창)이 먼저 카드를 골라 저장에서 이겼다(두 탭 경합): 그 칸을 같은 방식으로 공개한다.
   * 아직 고르지 않았으면 그 칸을 바로 열고(명령은 보내지 않는다), 이미 다른 칸을 골라 보여 줬으면 공개를 이긴 칸으로 바로잡는다.
   */
  reconcile(won: number): void {
    if (!this.opts || !Number.isInteger(won) || won < 0 || won > 2 || this.chosen === won) return;
    if (this.phase === 'fly' || this.phase === 'pick') { this.phase = 'pick'; this.choose(won, false, true); return; }
    if (this.phase !== 'reveal' || this.corrected) return;
    this.corrected = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    for (const a of this.anims) { try { a.cancel(); } catch { /* 이미 끝남 */ } }
    this.anims.length = 0;
    this.chosen = won;
    for (let k = 0; k < 3; k++) {
      const c = this.cards[k];
      c.root.classList.remove('dim', 'chosen', 'idle'); c.root.classList.add('lock', 'flipped', 'show');
      c.front.removeAttribute('aria-hidden');
      c.root.classList.toggle('missed', k !== won);
      c.tag.textContent = k === won ? '' : '놓친 보상';
    }
    this.title.textContent = '다른 창에서 먼저 고른 카드가 적용됐어요';
    this.glow(this.cards[won], this.cards[won].face!, true);
    this.revealedAt = performance.now();
    this.skip.classList.add('on');
    this.later(1800, () => this.close());
  }

  // ── 내부 ─────────────────────────────────────────
  private buildCard(i: number): CardEl {
    const root = el('div', '', this.row);
    root.className = 'dh-pk-card lock'; root.dataset.testid = 'card-' + i; root.tabIndex = -1;
    root.setAttribute('role', 'button'); root.setAttribute('aria-label', '보상 카드 ' + (i + 1));
    const rays = el('img', '', root); rays.className = 'dh-pk-rays'; rays.alt = ''; rays.draggable = false; rays.src = assetUrl('ui/cards/rays-gold.webp');
    rays.addEventListener('error', () => { rays.style.display = 'none'; });
    const inner = el('div', '', root); inner.className = 'dh-pk-in';
    const back = el('div', '', inner); back.className = 'dh-pk-back'; back.setAttribute('aria-hidden', 'true');
    const bi = el('img', '', back); bi.alt = ''; bi.draggable = false; bi.src = assetUrl(CARD_BACK);
    bi.addEventListener('error', () => back.classList.add('noimg'));
    el('div', '', back, '?').className = 'q';
    const front = el('div', '', inner); front.className = 'dh-pk-front';
    // 고른 카드 둘레 금빛 윤곽(ChatGPT GUI 키트 card-highlight). 실패하면 기존 CSS 빛만 남는다
    const hl = el('img', '', root); hl.className = 'dh-pk-hl'; hl.alt = ''; hl.draggable = false; hl.src = assetUrl(CARD_HIGHLIGHT);
    hl.addEventListener('error', () => { hl.style.display = 'none'; });
    const tag = el('div', '', root); tag.className = 'dh-pk-tag';
    root.addEventListener('click', (e) => { e.stopPropagation(); this.tryPick(i); });
    root.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.tryPick(i); } });
    return { root, inner, front, tag, rays, face: null };
  }

  private fillFront(c: CardEl, f: CardFace): void {
    c.front.textContent = '';
    c.front.classList.remove('art'); // 카드 재사용 때 이전 틀 클래스가 남지 않게(새 틀이 읽힐 때만 다시 붙인다)
    el('div', '', c.front, f.label).className = 'dh-pk-kind';
    const ic = el('div', '', c.front); ic.className = 'dh-pk-ic';
    if (f.icons.length > 0) {
      const img = el('img', '', ic); img.alt = ''; img.draggable = false;
      let k = 0;
      img.addEventListener('error', () => { if (++k < f.icons.length) img.src = assetUrl(f.icons[k]); else img.style.display = 'none'; });
      img.src = assetUrl(f.icons[0]);
    }
    // 긴 이름은 두 줄로 접고 글자를 줄인다(clearStyle .n2·.n3). 한글 한 글자 = 1칸
    const nameLen = Array.from(f.name).length;
    el('div', '', c.front, f.name).className = 'dh-pk-name' + (nameLen > 18 ? ' n3' : nameLen > 8 ? ' n2' : '');
    el('div', '', c.front, f.qty).className = 'dh-pk-qty';
    const fr = el('img', '', c.front); fr.className = 'dh-pk-fr'; fr.alt = ''; fr.draggable = false;
    fr.addEventListener('error', () => fr.classList.add('noimg'));
    fr.addEventListener('load', () => c.front.classList.add('art'));
    fr.src = assetUrl(CARD_FRAMES[f.rarity] ?? CARD_FRAMES[0]);
  }

  /** 카드 크기: 화면 높이·너비에 맞춘다(2:3 세로 카드, 폰 가로 568×320에서도 세 장이 한 줄) */
  private readonly layout = (): void => {
    const w = this.parent.clientWidth || window.innerWidth, h = this.parent.clientHeight || window.innerHeight;
    const chHeight = Math.min(h * (h < 420 ? 0.56 : 0.5), 360);
    const cwFromW = (w - 48 - 2 * Math.min(18, w * 0.03)) / 3;
    const cw = Math.max(70, Math.min(chHeight * 0.68, cwFromW));
    this.root.style.setProperty('--cw', Math.round(cw) + 'px');
    this.root.style.setProperty('--ch', Math.round(cw / 0.68) + 'px');
    this.sparks.resize(w, h);
  };

  private flyIn(): void {
    const w = this.parent.clientWidth || window.innerWidth, h = this.parent.clientHeight || window.innerHeight;
    const spins = [-540, 720, 540];
    const side = [-1, 0.2, 1];
    for (let i = 0; i < 3; i++) {
      const c = this.cards[i];
      const r = c.root.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      // 화면 밖(위쪽·좌우)에서 시작: 가운데 카드는 위에서, 양쪽은 옆 위에서
      const sx = w / 2 + side[i] * w * 0.75, sy = -r.height * 1.2;
      const dx = sx - cx, dy = sy - cy;
      const a = c.root.animate([
        { transform: `translate(${dx}px,${dy}px) rotate(${spins[i]}deg) scale(1.9)`, opacity: 0 },
        { opacity: 1, offset: 0.12 },
        { transform: 'translate(0,0) rotate(0deg) scale(1.08)', opacity: 1, offset: 0.8 },
        { transform: 'translate(0,0) rotate(0deg) scale(1)', opacity: 1 },
      ], { duration: TIMELINE.flyMs, delay: flyStartMs(i), easing: 'cubic-bezier(.2,.75,.25,1)', fill: 'backwards' });
      this.anims.push(a);
      this.later(flyStartMs(i), () => uiSfx('dodge'));
      this.later(flyStartMs(i) + TIMELINE.flyMs * 0.78, () => { uiSfx('cardLand'); this.dust(c); });
    }
  }

  private dust(c: CardEl): void {
    const d = el('div', '', c.root); d.className = 'dh-pk-dust';
    const a = d.animate([{ opacity: 0.9, transform: 'scale(.4)' }, { opacity: 0, transform: 'scale(1.6)' }], { duration: 420, easing: 'ease-out' });
    a.onfinish = () => d.remove();
  }

  private unlock(): void {
    for (const c of this.cards) { c.root.classList.remove('lock'); c.root.classList.add('idle'); c.root.setAttribute('aria-disabled', 'false'); c.root.tabIndex = 0; }
    this.cd.start();
    this.lastTs = 0;
    this.setHint('카드 한 장을 고르세요 · 시간이 지나면 자동으로 골라요', '');
    this.paintTimer();
  }

  private setHint(text: string, cls: '' | 'warn' | 'ok'): void {
    this.hint.textContent = text;
    this.hint.className = 'dh-pk-hint' + (cls ? ' ' + cls : '');
  }

  private paintTimer(): void {
    const s = this.cd.seconds;
    // 게이지는 매 프레임(값 하나), 숫자는 초가 바뀔 때만
    const off = Math.max(0, Math.min(200, Math.round(200 * (1 - this.cd.ratio))));
    if (off !== this.lastOff) { this.lastOff = off; this.ring.style.strokeDashoffset = RING_OFFSET[off]; }
    if (s !== this.lastSec) {
      this.lastSec = s;
      this.secText.textContent = SECOND_TEXT[s] ?? '';
      this.timer.classList.toggle('urgent', this.cd.urgent);
      if (this.phase === 'pick' && this.cd.urgent && s > 0) uiSfx('uiClick');
    }
  }

  /** 카드를 골랐다: 명령을 보내고, 들려 올라가 뒤집힌 뒤 빛을 내고, 나머지를 차례로 공개한다 */
  private choose(i: number, auto: boolean, external = false): void {
    if (this.phase !== 'pick' || !this.opts) return;
    this.phase = 'reveal'; this.chosen = i;
    this.cd.stop();
    const o = this.opts;
    if (!external) this.onPick(i, auto);
    this.timer.style.visibility = 'hidden';
    this.title.textContent = external ? '다른 창에서 먼저 고른 카드가 적용됐어요' : auto ? '시간 초과! 자동으로 골랐어요' : '이 카드를 골랐어요';
    this.setHint('', '');
    for (let k = 0; k < 3; k++) {
      const c = this.cards[k];
      c.root.classList.remove('idle'); c.root.classList.add('lock');
      c.root.setAttribute('aria-disabled', 'true'); c.root.tabIndex = -1;
      if (k !== i) c.root.classList.add('dim');
    }
    const chosen = this.cards[i], face = chosen.face!;
    if (auto) uiSfx('cardPick'); // 눌러 고른 소리는 오디오 엔진의 클릭 감지(audio/index.ts)가 낸다
    // 들어 올리고 → 뒤집는다
    if (!o.reducedMotion) {
      this.anims.push(chosen.root.animate([{ transform: 'translateY(0) scale(1)' }, { transform: 'translateY(-3%) scale(1.1)' }], { duration: TIMELINE.liftMs, easing: 'ease-out', fill: 'forwards' }));
    }
    this.later(o.reducedMotion ? 0 : TIMELINE.liftMs * 0.6, () => { chosen.root.classList.add('flipped'); chosen.front.removeAttribute('aria-hidden'); uiSfx('cardFlip'); });
    const revealAt = o.reducedMotion ? 80 : chosenRevealedMs();
    this.later(revealAt, () => this.glow(chosen, face, true));
    // 나머지 카드: 놓친 보상
    let j = 0;
    for (let k = 0; k < 3; k++) {
      if (k === i) continue;
      const c = this.cards[k];
      const at = o.reducedMotion ? revealAt + 200 + j * 120 : otherFlipMs(j);
      this.later(at, () => {
        c.root.classList.remove('dim'); c.root.classList.add('flipped', 'missed', 'show');
        c.front.removeAttribute('aria-hidden');
        c.tag.textContent = '놓친 보상';
        uiSfx('cardFlip');
      });
      j++;
    }
    const end = o.reducedMotion ? revealAt + 600 : allRevealedMs(3);
    this.later(end, () => {
      this.revealedAt = performance.now();
      this.skip.classList.add('on');
      this.setHint('', '');
    });
    this.later(end + TIMELINE.closeAfterMs, () => this.close());
  }

  /** 선택한 카드의 등급 빛: 테두리 맥동 + 파티클 + (영웅·전설) 광선 + (전설) 한 번 번쩍·흔들림 */
  private glow(c: CardEl, f: CardFace, chosen: boolean): void {
    const o = this.opts!;
    c.root.classList.add('show');
    c.tag.textContent = chosen ? '획득! · ' + f.rarityName : '';
    if (chosen) c.root.classList.add('chosen');
    const fx = RARITY_FX[f.rarity];
    this.setHint(`${f.name} ${f.qty}`, 'ok');
    const rr = this.root.getBoundingClientRect(), r = c.root.getBoundingClientRect();
    const px = r.left - rr.left + r.width / 2, py = r.top - rr.top + r.height / 2;
    if (!o.reducedMotion) this.sparks.burst(px, py, fx.burst, Math.min(rr.width, rr.height) * (0.5 + f.rarity * 0.18)); // 움직임 줄이기: 파티클 0(particleBudget)
    if (f.rarity >= 2 && !o.reducedMotion) {
      c.rays.style.filter = f.rarity === 2 ? 'hue-rotate(250deg) saturate(1.4)' : '';
      this.anims.push(c.rays.animate([{ opacity: 0, scale: '0.3' }, { opacity: f.rarity === 3 ? 0.95 : 0.6, scale: '1' }], { duration: 520, easing: 'ease-out', fill: 'forwards' }));
    }
    if (f.rarity === 3) {
      uiSfx('cardLegend');
      if (!o.reducedMotion) {
        // 번쩍은 한 번(섬광 1초 3회 이하), 흔들림은 위 6걸음 감쇠(무작위·지속 흔들림 없음)
        this.anims.push(this.flash.animate([{ opacity: 0 }, { opacity: o.reduceFlash ? 0.25 : 0.7, offset: 0.2 }, { opacity: 0 }], { duration: 380, easing: 'linear' }));
        if (!o.reduceFlash) {
          const s = Math.max(4, Math.round(Math.min(rr.width, rr.height) * 0.012));
          this.anims.push(this.row.animate([
            { transform: 'translate(0,0)' }, { transform: `translate(${s}px,${-s}px)`, offset: 0.12 }, { transform: `translate(${-s}px,${s * 0.6}px)`, offset: 0.3 },
            { transform: `translate(${s * 0.6}px,${-s * 0.3}px)`, offset: 0.5 }, { transform: `translate(${-s * 0.3}px,0)`, offset: 0.72 }, { transform: 'translate(0,0)' },
          ], { duration: 460, easing: 'ease-out' }));
        }
      }
    } else if (f.rarity >= 1) uiSfx('cardReveal');
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.phase === 'pick' && (e.key === '1' || e.key === '2' || e.key === '3')) { e.preventDefault(); this.tryPick(Number(e.key) - 1); }
    else if (this.phase === 'reveal' && this.revealedAt > 0 && (e.key === 'Enter' || e.key === ' ') && performance.now() >= this.revealedAt) { e.preventDefault(); this.close(); }
  };

  private later(ms: number, fn: () => void): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  /** QA: 파티클 상한·피크 */
  stats(): { peak: number; spawned: number; capacity: number } { return { peak: this.sparks.peak, spawned: this.sparks.spawned, capacity: this.sparks.capacity }; }
}
